/**
 * User menu dropdown - shows user info and logout option
 */
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { IoChevronDown, IoLogOut, IoPerson, IoSettings } from 'react-icons/io5';

import { useAuth } from '@/app/auth';
import SettingsModal from '@/features/settings/components/SettingsModal';

export default function UserMenu(): ReactElement {
  const { user, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close menu on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  if (!user) return <></>;

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
  };

  const handleOpenSettings = () => {
    setIsOpen(false);
    setIsSettingsOpen(true);
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="hover:bg-slate flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <img src={user.avatarUrl} alt={user.name} className="border-storm h-8 w-8 rounded-full border" />
        <span className="text-pearl hidden text-sm font-medium md:inline">{user.name}</span>
        <IoChevronDown className={`text-ash h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="border-storm bg-obsidian shadow-void/50 absolute top-full right-0 z-50 mt-2 w-56 rounded-lg border py-1 shadow-xl">
          {/* User info */}
          <div className="border-storm border-b px-4 py-3">
            <div className="flex items-center gap-3">
              <img src={user.avatarUrl} alt={user.name} className="border-storm h-10 w-10 rounded-full border" />
              <div className="min-w-0 flex-1">
                <p className="text-pearl truncate font-medium">{user.name}</p>
                <p className="text-ash truncate text-xs">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <button
              onClick={() => setIsOpen(false)}
              className="text-silver hover:bg-slate hover:text-pearl flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors"
            >
              <IoPerson className="h-4 w-4" />
              Profile
            </button>
            <button
              onClick={handleOpenSettings}
              className="text-silver hover:bg-slate hover:text-pearl flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors"
            >
              <IoSettings className="h-4 w-4" />
              Settings
            </button>
            <button
              onClick={handleSignOut}
              className="text-danger hover:bg-danger/10 flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors"
            >
              <IoLogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </div>
  );
}
